/**
 * DetailPageSkeleton - Full-page loading skeleton for detail/record pages.
 *
 * Replaces the full-screen centered spinner that several detail pages returned
 * on `isLoading`. Mirrors the shape of a real detail page (header block +
 * content cards) so the layout doesn't jump when data arrives, per the
 * UX Performance Rules (skeletons over spinners).
 *
 * Navigation is provided at the route level by ModuleLayout, so this renders
 * page content chrome only.
 *
 * @example
 *   if (isLoading) return <DetailPageSkeleton />;
 */
import { Card } from './Card';

interface DetailPageSkeletonProps {
  /** Number of content cards to render below the header. Default 4. */
  sectionCount?: number;
  /** Render an edge-to-edge container instead of the centered max-w-7xl. */
  fullWidth?: boolean;
}

export function DetailPageSkeleton({ sectionCount = 4, fullWidth = false }: DetailPageSkeletonProps) {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className={`${fullWidth ? 'w-full' : 'max-w-7xl mx-auto'} px-4 py-6`}>
        <div className="animate-pulse space-y-6" role="status" aria-label="Loading">
          {/* Header: title, subtitle, action buttons */}
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-3">
              <div className="h-7 w-64 rounded bg-slate-200" />
              <div className="h-4 w-40 rounded bg-slate-100" />
            </div>
            <div className="flex gap-2">
              <div className="h-10 w-24 rounded bg-slate-200" />
              <div className="h-10 w-24 rounded bg-slate-200" />
            </div>
          </div>

          {/* Content cards */}
          {Array.from({ length: sectionCount }).map((_, i) => (
            <Card key={i}>
              <div className="mb-3 h-4 w-1/3 rounded bg-slate-200" />
              <div className="mb-2 h-3 w-full rounded bg-slate-100" />
              <div className="mb-2 h-3 w-3/4 rounded bg-slate-100" />
              <div className="h-3 w-1/2 rounded bg-slate-100" />
            </Card>
          ))}

          <span className="sr-only">Loading…</span>
        </div>
      </div>
    </div>
  );
}
